import Button from 'tio-reimagined/components/button';
import { dropTask } from 'ember-concurrency';
import Input from 'tio-reimagined/components/input';
import Switch from 'tio-reimagined/components/switch';
import { on } from '@ember/modifier';
import { PhDeviceMobile, PhLock } from 'ember-phosphor-icons';
import { Section } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import formatPhone from 'tio-common/helpers/format-phone';
import Heading from 'tio-reimagined/components/heading';
import inputmask from 'tio-common/modifiers/inputmask';
import MfaVerification from './mfa-verification';
import Modal from 'tio-reimagined/components/modal';
import { not } from 'tio-ui/utilities';
import type EmployeeModel from 'tio-common/models/employee';
import type PersonModel from 'tio-common/models/person';
import type Owner from '@ember/owner';
import type PhoneNumber from 'tio-common/models/phone-number';
import type SessionContextService from '../../../../services/session-context';
import type SessionService from '../../../../services/session';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';

export interface SecuritySettingsSignature {
  Args: {
    employee?: EmployeeModel;
    familyPerson?: PersonModel;
  };
}

export default class SecuritySettings extends Component<SecuritySettingsSignature> {
  @service declare session: SessionService;
  @service declare sessionContext: SessionContextService;
  @service declare store: StoreService;

  @tracked isOpen = true;
  @tracked isLoading = false;
  @tracked error = null;
  @tracked isEditingContact = false;
  @tracked phoneValue!: string;
  @tracked verificationMode?: 'mfa-enable' | 'phone-change' | 'mfa-phone-entry';
  @tracked showPhoneChangeWarning = false;
  @tracked showMfaPhoneEntry = false;
  @tracked mfaPhoneEntryValue = '';
  @tracked codeSentAt?: number;
  @tracked codeSentForMode?: 'mfa-enable' | 'mfa-phone-entry';
  @tracked codeSentForPhone?: string;

  constructor(owner: Owner, args: SecuritySettingsSignature['Args']) {
    super(owner, args);
    this.setInitialPhoneValue();
  }

  get person() {
    return this.args.familyPerson ?? this.args.employee?.person;
  }

  get primaryPhoneNumber() {
    return this.args.employee?.primaryPhoneNumber ?? this.person?.primaryPhoneNumber;
  }

  setInitialPhoneValue() {
    const raw = this.primaryPhoneNumber?.number || '';
    const digits = raw.replace(/\D/g, '');
    // Normalize to +1XXXXXXXXXX so the mask can display it
    if (digits.length === 10) {
      this.phoneValue = `+1${digits}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
      this.phoneValue = `+${digits}`;
    } else {
      this.phoneValue = raw;
    }
  }

  get login() {
    return this.person?.user?.logins?.find((login) => login.isActive);
  }

  get userName() {
    return this.login ? this.login.userName : '';
  }

  get isMfaEnabled() {
    return this.login?.twoFactorEnabled ?? false;
  }

  get mfaPhoneNumber() {
    return this.primaryPhoneNumber?.number || this.phoneValue || '';
  }

  get isMfaToggleDisabled() {
    return this.updateMfaSettings.isRunning;
  }

  get showMfaPhonePrompt() {
    return !this.isPhoneNumberValid && !this.isMfaEnabled;
  }

  get showMfaPhoneNumber() {
    return !this.showMfaPhonePrompt && !!this.mfaPhoneNumber;
  }

  toggleExpand = () => {
    this.isOpen = !this.isOpen;
  };

  toggleContactEdit = () => {
    this.isEditingContact = !this.isEditingContact;
    this.setInitialPhoneValue();
  };

  setPhoneValue = (value: string) => {
    // Store raw value during editing; normalization happens on blur
    this.phoneValue = value || '';
  };

  onPhoneBlur = () => {
    this.normalizePhoneValue();
  };

  private normalizePhoneValue() {
    // Normalize phone to +1XXXXXXXXXX format
    const digits = this.phoneValue.replace(/\D/g, '');
    if (digits.length === 10) {
      this.phoneValue = `+1${digits}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
      this.phoneValue = `+${digits}`;
    }
  }

  get isPhoneNumberValid() {
    const digits = this.phoneValue.replace(/\D/g, '');
    return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
  }

  get isSubmitDisabled() {
    return !this.isPhoneNumberValid || this.phoneValue === this.primaryPhoneNumber?.number;
  }

  get normalizedPhoneValue() {
    const digits = this.phoneValue.replace(/\D/g, '');
    if (digits.length === 10) {
      return `+1${digits}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
      return `+${digits}`;
    }
    return this.phoneValue;
  }

  savePhoneNumber = dropTask(async () => {
    this.normalizePhoneValue();

    if (
      this.isMfaEnabled &&
      this.primaryPhoneNumber &&
      this.phoneValue !== this.primaryPhoneNumber.number
    ) {
      this.showPhoneChangeWarning = true;
      return;
    }

    await this.doSavePhone();
  });

  private async doSavePhone() {
    this.normalizePhoneValue();
    this.clearCodeSentState();
    const normalizedPhone = this.phoneValue;

    if (this.primaryPhoneNumber) {
      this.primaryPhoneNumber.isVerified = false;
      this.primaryPhoneNumber.number = normalizedPhone;
      await this.primaryPhoneNumber.save({
        adapterOptions: { changelog: 'updated by employee' },
      });
    } else if (this.person?.user) {
      const newPhoneNumber = this.store.createRecord<PhoneNumber>('phone-number', {
        contactType: 'TELEPHONE',
        number: normalizedPhone,
        phoneNumberType: 'MOBILE',
        user: this.person.user,
      });
      await newPhoneNumber.save({ adapterOptions: { changelog: 'added by employee' } });
    }

    if (this.args.employee) {
      await this.args.employee.reload({ include: 'phone-numbers' });
    } else if (this.args.familyPerson) {
      await this.args.familyPerson.reload({ include: 'phone-numbers' });
    }
    this.toggleContactEdit();
  }

  confirmPhoneChange = () => {
    this.showPhoneChangeWarning = false;
    this.verificationMode = 'phone-change';
  };

  cancelPhoneChange = () => {
    this.showPhoneChangeWarning = false;
  };

  get isMfaPhoneEntryValid() {
    const digits = this.mfaPhoneEntryValue.replace(/\D/g, '');
    return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
  }

  get normalizedMfaPhoneEntryValue() {
    const digits = this.mfaPhoneEntryValue.replace(/\D/g, '');
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    return this.mfaPhoneEntryValue;
  }

  setMfaPhoneEntryValue = (value: string) => {
    this.mfaPhoneEntryValue = value || '';
  };

  onMfaPhoneEntrySubmit = () => {
    if (!this.isMfaPhoneEntryValid) return;
    this.showMfaPhoneEntry = false;
    this.verificationMode = 'mfa-phone-entry';
  };

  onMfaPhoneEntryCancel = () => {
    this.showMfaPhoneEntry = false;
    this.mfaPhoneEntryValue = '';
  };

  updateMfaSettings = dropTask(async (isSelected: boolean) => {
    if (!this.login) {
      return;
    }

    if (isSelected) {
      // If a verification code was already sent for enabling MFA, reopen verification directly
      if (this.hasActiveCode && this.codeSentForMode === 'mfa-enable') {
        this.verificationMode = 'mfa-enable';
        return;
      }

      if (!this.isPhoneNumberValid) {
        // Preserve previously entered phone if a code was sent for it
        if (!(this.hasActiveCode && this.codeSentForMode === 'mfa-phone-entry')) {
          this.mfaPhoneEntryValue = '';
        }
        this.showMfaPhoneEntry = true;
        return;
      }

      const phone = this.primaryPhoneNumber;
      if (phone?.isVerified) {
        this.login.twoFactorEnabled = true;
        await this.login.save();
      } else {
        this.verificationMode = 'mfa-enable';
      }
    } else {
      this.login.twoFactorEnabled = false;
      await this.login.save();
    }
  });

  // --- Consolidated MfaVerification props ---

  get showVerification() {
    return !!this.verificationMode;
  }

  get verificationPhoneNumber() {
    if (this.verificationMode === 'mfa-phone-entry') {
      return this.normalizedMfaPhoneEntryValue;
    }
    return this.mfaPhoneNumber;
  }

  get verificationNewPhoneNumber() {
    if (this.verificationMode === 'phone-change') {
      return this.normalizedPhoneValue;
    }
    if (this.verificationMode === 'mfa-phone-entry') {
      return this.normalizedMfaPhoneEntryValue;
    }
    return undefined;
  }

  get verificationAutoSend() {
    if (this.verificationInitialStep === 'enter-code') return false;
    return this.verificationMode === 'phone-change' || this.verificationMode === 'mfa-phone-entry';
  }

  get hasActiveCode() {
    if (!this.codeSentAt || !this.codeSentForMode) return false;
    const TEN_MINUTES = 10 * 60 * 1000;
    return Date.now() - this.codeSentAt < TEN_MINUTES;
  }

  get verificationInitialStep() {
    if (!this.hasActiveCode || !this.verificationMode) return undefined;
    if (this.verificationMode !== this.codeSentForMode) return undefined;
    if (this.verificationPhoneNumber !== this.codeSentForPhone) return undefined;
    return 'enter-code' as const;
  }

  private clearCodeSentState() {
    this.codeSentAt = undefined;
    this.codeSentForMode = undefined;
    this.codeSentForPhone = undefined;
  }

  onCodeSent = () => {
    if (this.verificationMode !== 'phone-change') {
      this.codeSentAt = Date.now();
      this.codeSentForMode = this.verificationMode as 'mfa-enable' | 'mfa-phone-entry';
      this.codeSentForPhone = this.verificationPhoneNumber;
    }
  };

  onVerificationComplete = async () => {
    const mode = this.verificationMode;
    this.verificationMode = undefined;
    this.clearCodeSentState();

    if (mode === 'mfa-enable' || mode === 'mfa-phone-entry') {
      if (this.login) {
        this.login.twoFactorEnabled = true;
        await this.login.save();
      }
    }

    // Reload to get updated phone records
    if (this.args.employee) {
      await this.args.employee.reload({ include: 'phone-numbers' });
    } else if (this.args.familyPerson) {
      await this.args.familyPerson.reload({ include: 'phone-numbers' });
    }

    if (mode === 'phone-change' || mode === 'mfa-phone-entry') {
      this.setInitialPhoneValue();
    }
    if (mode === 'phone-change') {
      this.isEditingContact = false;
    }
    if (mode === 'mfa-phone-entry') {
      this.mfaPhoneEntryValue = '';
    }
  };

  onVerificationCancel = () => {
    const mode = this.verificationMode;
    this.verificationMode = undefined;

    // For phone-change, always clear codeSent state (security-sensitive)
    if (mode === 'phone-change') {
      this.clearCodeSentState();
    }

    // Only clear phone entry value if no active code was sent for this flow
    if (mode === 'mfa-phone-entry') {
      const codeSentForThisFlow = this.hasActiveCode && this.codeSentForMode === 'mfa-phone-entry';
      if (!codeSentForThisFlow) {
        this.mfaPhoneEntryValue = '';
      }
    }
  };

  <template>
    <Section>
      <:header>{{t "profile.security_settings"}}</:header>
      <:body>
        <div class="flex flex-col">
          <div class="flex flex-row mx-8 my-4">
            <div class="self-center w-1/4 font-semibold text-gray-700">
              {{t "profile.personal_info"}}
            </div>
            <div class="flex flex-col w-1/5">
              <span class="text-xs">{{t "login.first_name"}}</span>
              <span class="ml-4 text-xl my-4">{{this.person.firstName}}</span>
            </div>
            <div class="flex flex-col w-1/5">
              <span class="text-xs">{{t "login.last_name"}}</span>
              <span class="ml-4 text-xl my-4">{{this.person.lastName}}</span>
            </div>
            <div class="flex-col ml-auto w-1/6 text-gray-500 grid justify-items-center">
              <PhLock @size="24" @weight="fill" />
              <span class="text-xs">{{t "profile.to_edit_contact_support"}}</span>
            </div>
          </div>
          <hr />
          <div class="flex flex-row mx-8 my-4">
            <div class="self-center w-1/4 font-semibold text-gray-700">
              {{t "profile.username_login"}}
            </div>
            <div class="flex flex-col w-1/5">
              <span class="text-xs">{{t "profile.username"}}</span>
              <span class="text-xl my-4 ml-4">{{this.userName}}</span>
            </div>
            <div class="flex-col ml-auto w-1/6 text-gray-500 grid justify-items-center">
              <PhLock @size="24" @weight="fill" />
              <span class="text-xs">{{t "profile.to_edit_contact_support"}}</span>
            </div>
          </div>
          <hr />
          <div class="flex flex-row mx-8 my-4">
            <div class="self-center w-1/4 font-semibold text-gray-700">
              {{t "profile.contact_info"}}
            </div>
            <div class="flex flex-col">
              {{#if this.isEditingContact}}
                <Input
                  @value={{this.phoneValue}}
                  @type="tel"
                  @onChange={{this.setPhoneValue}}
                  @onBlur={{this.onPhoneBlur}}
                  @label={{t "profile.mobile_phone"}}
                  placeholder="+1 (###) ###-####"
                  {{inputmask mask="+1 (999) 999-9999"}}
                  data-test-phone-input
                />
              {{else if this.phoneValue}}
                <span class="text-xl my-4 ml-4">
                  {{formatPhone this.phoneValue}}
                </span>
              {{else}}
                <span class="text-xs my-4 ml-4 text-red-500">
                  {{t "profile.no_phone"}}
                </span>
              {{/if}}
            </div>
            <div class="flex flex-row justify-end self-center ml-auto w-1/6 gap-x-2">
              {{#if this.isEditingContact}}
                <Button
                  @appearance="outlined"
                  @intent="primary"
                  {{on "click" this.savePhoneNumber.perform}}
                  @isDisabled={{this.isSubmitDisabled}}
                  data-test-contact-save
                >
                  {{t "common.save"}}
                </Button>
                <Button
                  @appearance="outlined"
                  @intent="danger"
                  {{on "click" this.toggleContactEdit}}
                  data-test-contact-cancel
                >
                  {{t "common.cancel"}}
                </Button>
              {{else}}
                <Button
                  @appearance="outlined"
                  @intent="primary"
                  {{on "click" this.toggleContactEdit}}
                  class="self-auto"
                  data-test-contact-edit
                >
                  {{t "common.edit"}}
                </Button>
              {{/if}}
            </div>
          </div>
          <hr />
          <div class="flex flex-row mx-8 my-4" data-test-mfa-section>
            <div class="self-center w-1/4 font-semibold text-gray-700">
              {{t "profile.mfa.title"}}
            </div>
            <div class="flex flex-col w-2/5">
              <span class="text-xs">{{t "profile.mfa.status"}}</span>
              <span class="text-xl my-4 ml-4" data-test-mfa-status>
                {{if
                  this.isMfaEnabled
                  (t "profile.mfa.status_enabled")
                  (t "profile.mfa.status_disabled")
                }}
              </span>
              {{#if this.showMfaPhonePrompt}}
                <span class="text-xs text-red-500 ml-4" data-test-mfa-phone-required>
                  {{t "profile.mfa.phone_required"}}
                </span>
              {{else if this.showMfaPhoneNumber}}
                <span class="text-xs text-gray-500 ml-4" data-test-mfa-phone-number>
                  {{t "profile.mfa.sms_number"}}
                  {{formatPhone this.mfaPhoneNumber}}
                </span>
              {{/if}}
            </div>
            <div class="flex flex-row justify-end self-center ml-auto w-1/6 gap-x-2">
              <div data-test-mfa-toggle>
                <Switch
                  @checked={{this.isMfaEnabled}}
                  @onChange={{this.updateMfaSettings.perform}}
                  @isDisabled={{this.isMfaToggleDisabled}}
                  @label={{t "profile.mfa.toggle_label"}}
                />
              </div>
            </div>
          </div>
        </div>
      </:body>
    </Section>

    {{! MFA / Phone Verification Modal (consolidated) }}
    <MfaVerification
      @show={{this.showVerification}}
      @phoneNumber={{this.verificationPhoneNumber}}
      @newPhoneNumber={{this.verificationNewPhoneNumber}}
      @autoSend={{this.verificationAutoSend}}
      @initialStep={{this.verificationInitialStep}}
      @onVerified={{this.onVerificationComplete}}
      @onCancel={{this.onVerificationCancel}}
      @onCodeSent={{this.onCodeSent}}
    />

    {{! Phone Change Warning }}
    <Modal
      @isOpen={{this.showPhoneChangeWarning}}
      @onClose={{this.cancelPhoneChange}}
      @size="sm"
      data-test-phone-change-warning
    >
      <:header>
        <Heading @level={{3}} class="text-lg font-semibold text-gray-900">
          {{t "profile.mfa.phone_change_warning.title"}}
        </Heading>
      </:header>
      <:body>
        <p class="text-sm text-gray-600">
          {{t "profile.mfa.phone_change_warning.message"}}
        </p>
      </:body>
      <:footer>
        <div class="flex justify-end gap-3">
          <Button
            @appearance="outlined"
            @intent="default"
            {{on "click" this.cancelPhoneChange}}
            data-test-phone-change-cancel
          >
            {{t "profile.mfa.phone_change_warning.cancel"}}
          </Button>
          <Button
            @intent="primary"
            {{on "click" this.confirmPhoneChange}}
            data-test-phone-change-continue
          >
            {{t "profile.mfa.phone_change_warning.continue"}}
          </Button>
        </div>
      </:footer>
    </Modal>

    {{! MFA Phone Entry Modal (no phone on file) }}
    <Modal
      @isOpen={{this.showMfaPhoneEntry}}
      @onClose={{this.onMfaPhoneEntryCancel}}
      @size="md"
      data-test-mfa-phone-entry
    >
      <:header>
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
            <PhDeviceMobile class="text-violet-600" @size={{20}} />
          </div>
          <Heading @level={{2}} class="text-lg font-semibold text-gray-900">
            {{t "profile.mfa.phone_entry.title"}}
          </Heading>
        </div>
      </:header>
      <:body>
        <div class="space-y-4">
          <p class="text-sm text-gray-600">
            {{t "profile.mfa.phone_entry.description"}}
          </p>
          <Input
            @value={{this.mfaPhoneEntryValue}}
            @type="tel"
            @onChange={{this.setMfaPhoneEntryValue}}
            @label={{t "profile.mobile_phone"}}
            placeholder="+1 (###) ###-####"
            {{inputmask mask="+1 (999) 999-9999"}}
            data-test-mfa-phone-entry-input
          />
        </div>
      </:body>
      <:footer>
        <div class="flex justify-end gap-3">
          <Button
            @appearance="outlined"
            @intent="default"
            {{on "click" this.onMfaPhoneEntryCancel}}
            data-test-mfa-phone-entry-cancel
          >
            {{t "common.cancel"}}
          </Button>
          <Button
            @intent="primary"
            @isDisabled={{not this.isMfaPhoneEntryValid}}
            {{on "click" this.onMfaPhoneEntrySubmit}}
            data-test-mfa-phone-entry-continue
          >
            {{t "profile.mfa.phone_entry.continue"}}
          </Button>
        </div>
      </:footer>
    </Modal>
  </template>
}
